import type { DataSource, Entity, WebcamEntity } from '../types/entity';
import { apiFetch } from '../lib/apiFetch';

/**
 * DER-SP — dataset oficial "Câmeras" (Dados Abertos SP, CC-BY 4.0),
 * pré-processado e gravado no Firestore via script standalone
 * (functions/src/ingest/derSpCameras.ts), servido aqui por um endpoint
 * HTTP estático simples. Não é uma API em tempo real: o dataset é
 * atualizado esporadicamente pelo DER-SP, e o refresh no client segue
 * essa cadência (ver POLL_INTERVAL_MS em LayerOrchestrator.ts).
 *
 * IMPORTANTE: esta fonte NÃO tem previewUrl nem playerUrl. O dataset
 * oficial do DER-SP não fornece URL de imagem ou vídeo por câmera — só
 * localização e status (pública ou de uso restrito). Ver o comentário
 * de topo do script de ingestão pra detalhes de por que essa lacuna
 * existe na fonte oficial. `previewUrl`/`playerUrl` ficam `null`
 * sempre; o valor desta camada é geográfico/situacional (onde há
 * câmeras e quantas), não de streaming.
 *
 * Uma `Entity` é gerada POR CÂMERA, não por ponto — um ponto com 2
 * câmeras (`cameraCount: 2`) produz 2 entidades na mesma coordenada,
 * consistente com o padrão de "uma entidade = uma câmera" usado nas
 * outras camadas de webcam (Windy, WSDOT).
 */

interface DerSpProxyPoint {
  id: string;
  regional: string;
  stateRoute: number;
  roadName: string;
  km: number;
  city: string;
  lat: number;
  lon: number;
  cameraCount: number;
  cameras: ('public' | 'restricted')[];
}

interface DerSpProxyResponse {
  points: DerSpProxyPoint[];
}

function pointToEntities(point: DerSpProxyPoint): WebcamEntity[] {
  return point.cameras.map((status, index) => {
    const label = `SP-${point.stateRoute} km${point.km} — ${point.roadName} (${point.city})`;

    return {
      id: `webcam-dersp-${point.id}-${index}`,
      layer: 'webcams-der-sp',
      lat: point.lat,
      lon: point.lon,
      label: status === 'restricted' ? `${label} [uso restrito]` : label,
      // 'restricted' não é um alerta operacional — é só uma câmera que
      // existe mas não é exibida ao público. Mantém severity 'normal';
      // o rótulo já comunica a diferença sem usar o âmbar de alerta,
      // que é reservado a eventos críticos.
      severity: 'normal',
      observedAt: new Date().toISOString(),
      raw: {
        title: label,
        previewUrl: null, // fonte oficial não fornece — ver nota de topo
        playerUrl: null,
        source: 'der-sp',
      },
    };
  });
}

export const derSpCamerasSource: DataSource = {
  layer: 'webcams-der-sp',
  label: 'Câmeras (DER-SP)',
  requiresClientKey: false,
  viaCloudFunction: true,

  async fetchEntities(): Promise<Entity[]> {
    const res = await apiFetch('/der-sp/cameras');
    if (!res.ok) {
      throw new Error(`Proxy DER-SP respondeu ${res.status}`);
    }

    const data = (await res.json()) as DerSpProxyResponse;
    return data.points.flatMap(pointToEntities);
  },
};
